import { useState, type FormEvent } from "react";

const FORM_ENDPOINT = "https://readdy.ai/api/form/datvkthggrtvtk1bg7sg";

const SERVICES = [
  "Criação de site",
  "Loja virtual",
  "Hospedagem",
  "VPS / Servidores",
  "Domínio e e-mail profissional",
  "SEO",
  "Marketing digital",
  "Google Ads",
  "Landing page",
  "Criação de logotipo",
];

type StatusState = "idle" | "loading" | "success" | "error";

interface FormFeedback {
  state: StatusState;
  message: string;
}

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100";

export default function BudgetForm() {
  const [feedback, setFeedback] = useState<FormFeedback>({ state: "idle", message: "" });

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setFeedback({
        state: "success",
        message: "Recebemos o seu orçamento. Em breve a nossa equipe entra em contato.",
      });
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      if (typeof value !== "string" || value.trim() === "") return;
      payload.append(key, value);
    });

    setFeedback({ state: "loading", message: "" });

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = responseText ? (JSON.parse(responseText) as FormResponse) : null;
      } catch {
        parsed = null;
      }

      const serverMessage = String(
        parsed?.meta?.message ?? parsed?.message ?? parsed?.meta?.detail ?? responseText ?? ""
      ).trim();

      const isSpam = /spam/i.test(serverMessage);
      const isSuccess = response.ok && parsed?.code === "OK" && !isSpam;

      if (!isSuccess) {
        setFeedback({
          state: "error",
          message:
            serverMessage ||
            "Não foi possível enviar o seu orçamento agora. Tente novamente em instantes.",
        });
        return;
      }

      setFeedback({
        state: "success",
        message: "Recebemos o seu orçamento. Em breve a nossa equipe entra em contato.",
      });
      form.reset();
    } catch {
      setFeedback({
        state: "error",
        message:
          "Não foi possível enviar o seu orçamento. Verifique a sua conexão e tente novamente.",
      });
    }
  }

  const loading = feedback.state === "loading";

  return (
    <form
      id="budget-form-nxhost"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-2xl border border-background-200 bg-background-50 p-6 md:p-10"
    >
      <div className="mb-8 max-w-2xl">
        <span className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-primary-700">
          <i className="ri-file-list-3-line" aria-hidden="true"></i>
          Orçamento
        </span>
        <h2 className="mt-5 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
          Solicite um orçamento
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-foreground-600">
          Conte o que você precisa e a nossa equipe retorna com a proposta ideal para o seu
          negócio. Preencha os seus dados e marque os serviços de interesse.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="orc-nome" className="mb-1.5 block text-sm font-medium text-foreground-800">
            Nome completo
          </label>
          <input
            id="orc-nome"
            name="nome"
            type="text"
            required
            autoComplete="name"
            placeholder="Seu nome"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="orc-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
            E-mail
          </label>
          <input
            id="orc-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="voce@empresa.com.br"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="orc-telefone" className="mb-1.5 block text-sm font-medium text-foreground-800">
            Telefone / WhatsApp
          </label>
          <input
            id="orc-telefone"
            name="telefone"
            type="tel"
            required
            autoComplete="tel"
            placeholder="(54) 99999-9999"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="orc-empresa" className="mb-1.5 block text-sm font-medium text-foreground-800">
            Empresa
          </label>
          <input
            id="orc-empresa"
            name="empresa"
            type="text"
            autoComplete="organization"
            placeholder="Nome da sua empresa"
            className={inputClass}
          />
        </div>

        <div className="sm:col-span-2">
          <span className="mb-2 block text-sm font-medium text-foreground-800">
            Serviços de interesse
          </span>
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((service) => (
              <label
                key={service}
                className="flex cursor-pointer items-center gap-3 rounded-lg border border-background-200 bg-background-100/60 px-4 py-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50 has-[:checked]:text-primary-800"
              >
                <input
                  type="checkbox"
                  name="servicos"
                  value={service}
                  className="h-4 w-4 accent-primary-500"
                />
                <span>{service}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="orc-mensagem" className="mb-1.5 block text-sm font-medium text-foreground-800">
            Descreva o seu projeto
          </label>
          <textarea
            id="orc-mensagem"
            name="mensagem"
            required
            maxLength={500}
            rows={5}
            placeholder="Conte o objetivo, prazos e referências do seu projeto..."
            className={`${inputClass} resize-none`}
          ></textarea>
          <p className="mt-1.5 text-xs text-foreground-500">Máximo de 500 caracteres.</p>
        </div>
      </div>

      <div className="nx-honeypot-field" aria-hidden="true">
        <label htmlFor="company_alt">Não preencha este campo</label>
        <input
          id="company_alt"
          name="company_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      {feedback.state === "error" ? (
        <div className="mt-6 flex items-start gap-3 rounded-lg border border-foreground-300 bg-background-100 px-4 py-3">
          <i className="ri-error-warning-line mt-0.5 text-lg text-foreground-700" aria-hidden="true"></i>
          <p className="text-sm text-foreground-800">{feedback.message}</p>
        </div>
      ) : null}

      {feedback.state === "success" ? (
        <div className="mt-6 flex items-start gap-3 rounded-lg border border-secondary-200 bg-secondary-50 px-4 py-3">
          <i className="ri-checkbox-circle-line mt-0.5 text-lg text-secondary-700" aria-hidden="true"></i>
          <p className="text-sm text-secondary-900">{feedback.message}</p>
        </div>
      ) : null}

      <button
        type="submit"
        disabled={loading}
        className="mt-8 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-8 py-4 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
      >
        {loading ? (
          <>
            <i className="ri-loader-4-line animate-spin text-lg" aria-hidden="true"></i>
            Enviando...
          </>
        ) : (
          <>
            <i className="ri-send-plane-line text-lg" aria-hidden="true"></i>
            Solicitar orçamento
          </>
        )}
      </button>
    </form>
  );
}